/**
 * A REQUIREMENTS DOCUMENT, WRITTEN FROM THE BOARD RATHER THAN FILLED IN — the
 * documents programme, increment 2.
 *
 * WHY THIS EXISTS. A card IS a requirement (agile): captured once, it is both a
 * backlog item and a line in the requirements document. FlightDeck already
 * generates its hardest document — the change request, assembled live from the
 * backlog — yet Requirements was, until now, a blank file somebody uploads. That
 * made the app hand-do the one assembly it already automates, and it let the
 * requirements a customer signs drift from the cards the team actually builds.
 * The pm-witness (15 September 2026) named this the single place the app made a
 * PM re-key what the board already holds.
 *
 * SO REQUIREMENTS IS GENERATED, THE SAME WAY THE CHANGE REQUEST IS. This file is
 * the part that has to be right — "given these cards, what does the document
 * say" — and it is pure functions so it can be checked without a browser. The
 * screen (RequirementsDocument.tsx) renders what these return; the narrative
 * around the list (purpose, scope, stakeholders, assumptions) comes from the
 * project's charter fields, seeded on the screen, not here.
 *
 * WHAT IS NOT HERE, ON PURPOSE. Nothing formats a PDF and nothing decides scope
 * beyond the board's own answer. A requirement is deferred when the board says
 * so — blocked, or dropped — exactly as the change request decides it, so the
 * two documents never disagree about what was held back. Grouping is by epic and
 * ordering is by reference, so generating the same backlog twice produces the
 * same document — which matters for something that gets signed and compared
 * against the last reading.
 */
import { blockerOf } from './blocked';
import type { Issue, MoscowLevel } from './types';
import type { Project, Stakeholder } from './projects';
import type { RichDoc } from './rich-text';

/** One requirement, as the document prints it. `why` appears on deferred ones. */
export interface RequirementLine {
  id: string;
  ref: string;
  title: string;
  /** The card's plain-English explanation for a non-technical reader, if it has one. */
  summary?: string;
  /** How essential for this release — Must / Should / Could / Won't, or null when unset. */
  moscow?: MoscowLevel | null;
  /** Only on deferred items — the board's own reason it is held back. */
  why?: string;
}

/** A group of requirements under one heading — an epic on the board. */
export interface RequirementArea {
  name: string;
  items: RequirementLine[];
}

/**
 * HOW MANY REQUIREMENTS SIT AT EACH LEVEL — the release-necessity summary a
 * reviewer reads to see the shape of the release. `unset` is counted separately and
 * honestly: a requirement nobody has weighed is not the same as one weighed and
 * found to be a Could, and a summary that folded the two would overstate how
 * much of the release has actually been decided.
 */
export interface MoscowCounts {
  must: number;
  should: number;
  could: number;
  wont: number;
  unset: number;
}

export interface Requirements {
  /** The requirements in scope, grouped by area (epic), each area ordered by reference. */
  areas: RequirementArea[];
  /** Held back — blocked or dropped — carrying the board's own reason. */
  deferred: RequirementLine[];
  /** The MoSCoW roll-up across the in-scope requirements. */
  counts: MoscowCounts;
  /** Every in-scope requirement counted once. Deferred is reported separately. */
  total: number;
}

/**
 * The heading a requirement with no epic is filed under.
 *
 * A CARD WITH NO EPIC IS NOT DROPPED — it goes under a plain heading of its own,
 * because quietly leaving it out produces a document that looks complete and is
 * not, and nobody reading it could tell. The same rule the change request keeps.
 */
const UNFILED = 'Other requirements';

export function requirementsFrom(issues: Issue[]): Requirements {
  const line = (i: Issue): RequirementLine => ({
    id: i.id,
    ref: i.ref ?? '',
    title: i.title ?? '',
    ...(i.summary?.trim() ? { summary: i.summary.trim() } : {}),
    moscow: i.moscow ?? null,
    ...(blockerOf(i) ? { why: blockerOf(i) } : {}),
  });

  /* Deferred is decided by the board, not by the person writing — a requirement
     is held back when it is blocked or when it has been dropped, the two ways
     work does not get done. Both carry their reason already. Mirrors the change
     request so the two documents agree on what was set aside. */
  const isDeferred = (i: Issue) => !!blockerOf(i) || i.status === 'wontdo';
  const byRef = (a: Issue, b: Issue) => (a.ref ?? '').localeCompare(b.ref ?? '', undefined, { numeric: true });

  const deferred = issues.filter(isDeferred).sort(byRef).map(line);

  const grouped = new Map<string, Issue[]>();
  for (const i of issues.filter((x) => !isDeferred(x))) {
    const name = i.epic?.trim() || UNFILED;
    grouped.set(name, [...(grouped.get(name) ?? []), i]);
  }

  const areas: RequirementArea[] = [...grouped.entries()]
    .sort(([a], [b]) => {
      // The unfiled heading sits last however it sorts: it is a leftover, not a
      // section somebody planned.
      if (a === UNFILED) return 1;
      if (b === UNFILED) return -1;
      return a.localeCompare(b);
    })
    .map(([name, items]) => ({ name, items: items.sort(byRef).map(line) }));

  const counts: MoscowCounts = { must: 0, should: 0, could: 0, wont: 0, unset: 0 };
  for (const area of areas) {
    for (const item of area.items) {
      if (item.moscow) counts[item.moscow] += 1;
      else counts.unset += 1;
    }
  }

  return { areas, deferred, counts, total: areas.reduce((n, a) => n + a.items.length, 0) };
}

/**
 * THE WHOLE REQUIREMENTS DOCUMENT, COMPOSED — the requirement lines above PLUS the
 * narrative around them, which the screen reads from the project's About and the
 * PDF prints on its face.
 *
 * WHY THIS EXISTS, AND WHY IT IS ONE OBJECT. Requirements is generated from two
 * places that both move — the backlog (the lines) and About (purpose, scope,
 * stakeholders, assumptions, constraints) — so a document produced and signed has
 * to freeze BOTH, or a card corrected or an About field edited afterwards silently
 * changes what the signed document showed. The change request learned this the
 * hard way (Doc.scope, migration 0088). Composing the two into one value means the
 * snapshot is one write, the PDF takes one argument, and the screen renders the
 * frozen copy and the live copy through the same shape.
 *
 * PURE, like requirementsFrom: given a project and its requirements, the same
 * document. The screen seeds `requirements` by calling requirementsFrom on the
 * cards it covers; nothing here reads a store or a clock.
 */
export interface RequirementsDoc {
  /** The project's name as it stood — frozen so a later rename cannot change a signed document. */
  projectName: string;
  /** Section 1, the project's own description — rich text (0134). */
  purpose?: RichDoc | null;
  /** The measure of success, printed under Purpose when the project has one — rich text (0134). */
  successMeasure?: RichDoc | null;
  /** Section 2, rich text from About. */
  scopeIn?: RichDoc | null;
  scopeOut?: RichDoc | null;
  /** Section 3 — the sponsor is named first, then the rest. */
  sponsor?: string | null;
  stakeholders: Stakeholder[];
  /** Section 6, rich text from About. */
  assumptions?: RichDoc | null;
  constraints?: RichDoc | null;
  /** Sections 4, 5 and 7 — the backlog-derived requirement lines and their roll-up. */
  requirements: Requirements;
}

/** The project fields the narrative needs — a subset of Project, so a test can
 *  compose a document without standing up a whole project. */
type NarrativeSource = Pick<
  Project,
  | 'name'
  | 'description'
  | 'successMeasure'
  | 'scopeIn'
  | 'scopeOut'
  | 'sponsor'
  | 'stakeholders'
  | 'assumptions'
  | 'constraints'
>;

export function composeRequirements(
  project: NarrativeSource | undefined,
  requirements: Requirements
): RequirementsDoc {
  return {
    projectName: project?.name?.trim() || 'Untitled project',
    purpose: project?.description ?? null,
    successMeasure: project?.successMeasure ?? null,
    scopeIn: project?.scopeIn ?? null,
    scopeOut: project?.scopeOut ?? null,
    sponsor: project?.sponsor?.trim() || null,
    stakeholders: (project?.stakeholders ?? []).filter((s) => s.name?.trim()),
    assumptions: project?.assumptions ?? null,
    constraints: project?.constraints ?? null,
    requirements,
  };
}
